import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { TabbedNavigation, TabbedNavigationItem } from '@amzn/storm-ui';
import { useQuery } from '@tanstack/react-query';
import { CustomCard } from '../../../UiComponentsLibrary/CustomCard';
import CustomSpinner from '../../../UiComponentsLibrary/CustomSpinner';
import CustomAlert from '../../../UiComponentsLibrary/CustomAlert';
import { ReusableTable } from '../../../Shared/components/ReusableTable/ReusableTable';
import { ErrorDisplay } from '../../../Shared/components/ErrorDisplay/ErrorDisplay';
import { ApiError } from '../../../Shared/interfaces/ApiError';
import { getSlotPackageDisplayName } from '../../../BookingManagement/components/CreateBooking/constants/DetailedBookingConstants';
import useApiClient from '../../../Shared/services/api';
import { useAppContext } from '../../../Shared/store/appContext';
import { tabLinks, getTabConfigByLabel } from './InventoryViewConstants';
import { tableConfigs, queryConfigs } from '../../config/tableConfigs';
import { InventorySlotPackage } from '../../interfaces/InventoryDashboardInterfaces';
import { TabbedNavigationWrapper } from './InventoryView.styles';

const InventoryView: React.FC = () => {
  const [activeTab, setActiveTab] = useState<string>(tabLinks[0].label);
  const { t } = useTranslation();
  const api = useApiClient();
  const { state } = useAppContext();
  // Resolve the query config by the active tab's key (not by position) so a tab always
  // fetches its own slot's data regardless of TAB_CONFIG ordering.
  const activeTabKey = (getTabConfigByLabel(activeTab) ?? tabLinks[0]).key;
  const activeConfig = queryConfigs[activeTabKey];
  const artEventId = state.selectedArtEventId;

  const { error, isLoading, isError, data } = useQuery<
    typeof activeConfig.dataType,
    ApiError
  >({
    queryKey: [activeConfig?.queryKey, artEventId],
    queryFn: () => activeConfig?.queryFn(api, artEventId),
    enabled: !!activeConfig && !!artEventId,
  });

  const getActiveTabConfig = () => {
    return getTabConfigByLabel(activeTab);
  };

  const getSegmentKeysForTab = () => {
    return getActiveTabConfig()?.segmentKeys || [];
  };

  const getTableHeadersForTab = () => {
    const config = getActiveTabConfig();
    return config?.tableHeaders.map((header) => t(header)) || [];
  };

  const transformedData = useMemo(() => {
    if (!data?.slotPackages) return [];

    const segmentKeys = getSegmentKeysForTab();
    const segmentNames = [
      ...new Set(
        data.slotPackages.flatMap((pkg: InventorySlotPackage) =>
          pkg.segments.map((seg) => seg.name)
        )
      ),
    ];
    return segmentNames.map((segmentName) => {
      const row: any = { name: segmentName };

      data.slotPackages.forEach((pkg: InventorySlotPackage) => {
        const segment = pkg.segments.find((seg) => seg.name === segmentName);

        segmentKeys.forEach((key) => {
          const value = segment?.[key as keyof typeof segment];
          row[`${pkg.slotPackageName}_${key}`] =
            value !== undefined && value !== null ? value : '-';
        });
      });

      // Format all numeric values to localeString
      Object.keys(row).forEach((key) => {
        if (typeof row[key] === 'number') {
          row[key] = row[key].toLocaleString('en-US');
        }
      });

      return row;
    });
  }, [data, activeTab]);

  const createTotalValues = (): Record<string, number> => {
    const totalValues: Record<string, number> = {};
    const currentTabConfig = getActiveTabConfig();

    if (!currentTabConfig || !data?.slotPackages) {
      return totalValues;
    }

    data.slotPackages.forEach((pkg: InventorySlotPackage) => {
      currentTabConfig.totalValueKeys.forEach((key) => {
        const sourceProperty = currentTabConfig.totalValueSources[key];
        totalValues[`${pkg.slotPackageName}_${key}`] =
          (pkg[sourceProperty] as number) || 0;
      });
    });

    return totalValues;
  };

  const renderTabContent = () => {
    const config = tableConfigs[activeTab];
    const totalColumns = config.getTotalColumns
      ? config.getTotalColumns(data)
      : config.totalColumns;

    if (isLoading) return <CustomSpinner />;
    if (isError) return <ErrorDisplay error={error as ApiError} />;

    const tableHeaders = data?.slotPackages
      ? [
          t('inventory-dashboard-name-label'),
          ...data.slotPackages.flatMap(() => getTableHeadersForTab()),
        ]
      : [];
    // Render Superheaders only if the slotPackages array length is more than 1
    const superHeaders =
      data?.slotPackages?.length > 1
        ? [
            '',
            ...data.slotPackages.map(
              (pkg: InventorySlotPackage) => getSlotPackageDisplayName(pkg.slotPackageName)
            ),
          ]
        : [];
    const totalValues = createTotalValues();

    return (
      <ReusableTable
        optionalSuperTHeaders={superHeaders}
        tableHeaders={tableHeaders}
        totalColumns={totalColumns}
        totalValues={totalValues}
        rowsData={transformedData}
        renderRow={(item: any) => (
          <>
            {Object.keys(item).map((key) => (
              <td key={key}>{item[key]}</td>
            ))}
          </>
        )}
      />
    );
  };

  return (
    <CustomCard id="inventory-view-card" title={t('inventory-view-header')}>
      {artEventId ? (
        <>
          <TabbedNavigationWrapper>
            <TabbedNavigation>
              {tabLinks.map(({ label }) => (
                <TabbedNavigationItem
                  id={`inventory-tab-${t(label)}`}
                  key={label}
                  active={activeTab === label}
                  label={t(label)}
                  onClick={() => setActiveTab(label)}
                />
              ))}
            </TabbedNavigation>
          </TabbedNavigationWrapper>

          {renderTabContent()}
        </>
      ) : (
        <CustomAlert
          alertMessage={`${t('no-inventory-data-found')} ${t(
            'create-art-event-info-message'
          )}`}
        />
      )}
    </CustomCard>
  );
};

export default InventoryView;
